import React, { memo, useEffect, useState, useRef, useCallback, useMemo } from 'react';
import {
  isObjectEmpty,
  hasCMSwUpdatePermission,
  createApiPolling,
  capitalizeFirstLetter,
  isArraysEqual
} from 'common/utils/helpers';
import { isDeviceOnline } from 'common/utils/deviceTwinUtils';
import classnames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { bulkUpdateSoftware, updatePivotOverviewPageSelectedDevices, searchDevicesV2 } from 'redux/actions';
import BulkSoftwareDownloadModal from 'components/features/SoftwareDownloadModal/BulkSoftwareDownloadModal/BulkSoftwareDownloadModal';
import ActionDetailsModal from 'components/features/Actions/ActionDetailsModal/ActionDetailsModal';
import { IconAndText } from 'components/shared/SvgAF/SvgAF';
import { IconLoader } from 'common/IconLoader';
import { BUTTON_LOADING_CIRCLE, PERMISSION_ERROR_MESSAGE } from 'common/utils/constants';
import { getBulkSoftwareDetails } from 'apis/GetBulkSoftwareDetails';
import CommissionErrorBox from 'components/features/Commissioning/CommissionErrorBox/CommissionErrorBox';
import { getActions } from 'apis/GetActions';
import moment from 'moment';
import LoadingIndicator from 'components/shared/LoadingIndicator/LoadingIndicator';

const TERMINAL_SW_UPDATE_STATUSES = ['Completed', 'Failed', 'Canceled', 'Terminated'];

const BundleOverviewControllerSwUpdateActionContainer = (props) => {
  const dispatch = useDispatch();
  const [isBulkSWUpdateModalOpen, setIsBulkSWUpdateModalOpen] = useState(false);
  const [isActionDetailsModalOpen, setIsActionDetailsModalOpen] = useState(false);
  const [isActionPending, setIsActionPending] = useState(false);
  const [swUpdateError, setSwUpdateError] = useState(null);
  const [swUpdateAction, setSwUpdateAction] = useState(null);
  const [isPollingSwUpdateStatus, setIsPollingSwUpdateStatus] = useState(false);
  const [fetchedActiveAction, setFetchedActiveAction] = useState(false);
  const softwareVersions = useSelector((store) => store.data.softwareVersions);
  const bundles = useSelector((store) => store.data.bundlesStorage.storedBundlesDictionary);
  const permissions = useSelector((store) => store.data.rolePermissions.permissions);
  const isPivotHub = props.isPivotHub;
  const hasSwUpdatePermission = useMemo(() => hasCMSwUpdatePermission(permissions), [permissions]);

  const mounted = useRef(false);
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
    }

    return () => {
      mounted.current = false;
    };
  }, []);

  const getActiveActions = useCallback(() => {
    const default_from = moment().subtract(1, 'months').toISOString();
    getActions('active', default_from)
      .then((result) => {
        let matchedSwUpdateInstance = null;
        const memberIds = props.bundle.members.map((member) => member.deviceId);
        if (result && Array.isArray(result?.instances)) {
          for (let instance of result.instances) {
            if (isArraysEqual(memberIds, instance?.input?.device_list)) {
              matchedSwUpdateInstance = instance;
              break;
            }
          }
        }
        setSwUpdateAction(matchedSwUpdateInstance);
      })
      .catch((error) => {
        console.error(error);
      });
  }, [props.bundle?.members]);

  useEffect(() => {
    if (props.bundle && !fetchedActiveAction) {
      setFetchedActiveAction(true);
      !swUpdateAction && getActiveActions();
    }
  }, [props.bundle, fetchedActiveAction, getActiveActions]);

  useEffect(() => {
    if (swUpdateAction || props.device) {
      let swUpdateActionInstanceId = null;
      if (swUpdateAction) {
        swUpdateActionInstanceId = swUpdateAction.instance_id;
      } else if (props.device) {
        const swUpdateStatusDeviceInstanceId = props.device?.tags?.iotHubDM?.softwareDownload?.updateInstanceId;
        swUpdateActionInstanceId = swUpdateStatusDeviceInstanceId && swUpdateStatusDeviceInstanceId.split(':')?.[0];
      }

      if (swUpdateActionInstanceId && !isPollingSwUpdateStatus) {
        setIsPollingSwUpdateStatus(true);
        pollSWUpdateStatus(swUpdateActionInstanceId);
      }
    }
  }, [swUpdateAction, props.device, isPollingSwUpdateStatus]);

  const updateDevices = useCallback(
    (deviceIds) => {
      if (!deviceIds || deviceIds.length === 0) return;
      const cb_success = (result) => {
        // create a object that formats as {deviceId: deviceObj, ...}
        const updatedDevices = result.devices.reduce((acc, cur) => {
          acc[cur.deviceId] = cur;
          return acc;
        }, {});

        dispatch(updatePivotOverviewPageSelectedDevices(updatedDevices));
      };

      const searchDevicesV2Body = {
        size: deviceIds.length,
        coalesceBundles: false,
        filters: {
          deviceId: deviceIds
        }
      };

      dispatch(searchDevicesV2(searchDevicesV2Body, null, cb_success));
    },
    [dispatch]
  );

  const pollSWUpdateStatus = async (instanceId) => {
    const checkCallback = (result) => {
      console.log('SW Update Status: ', result?.runtime_status);
      if (!mounted.current) return true;
      setSwUpdateAction(result);
      if (TERMINAL_SW_UPDATE_STATUSES.includes(result?.runtime_status)) {
        updateDevices(result?.input?.device_list);
        return true;
      }
      return false;
    };
    const poll = createApiPolling(getBulkSoftwareDetails, checkCallback, { delay: 8000 });

    try {
      await poll(instanceId);
      console.log('Stopped polling SW Update status.');
    } catch (error) {
      console.error(error);
    }
  };

  const handleSwUpdateModalOnClose = () => {
    setIsBulkSWUpdateModalOpen(false);
  };

  const handleInitiateBulkSwUpgrade = (sentSelectedDeviceIds, swId, transferDate, activationDate) => {
    /* dispatch bulk update, use callbacks to then update state of actions status */
    setIsPollingSwUpdateStatus(false);
    setIsActionPending(true);
    dispatch(
      bulkUpdateSoftware(
        sentSelectedDeviceIds,
        swId,
        transferDate,
        activationDate,
        (result) => {
          setIsActionPending(false);
          setSwUpdateAction(result);
          setIsBulkSWUpdateModalOpen(false);
        }, //, date:datetime}})},
        (error) => {
          console.error(error);
          setSwUpdateError(error.message);
          setIsActionPending(false);
        }
      )
    );
  };

  const handleSWDownloadError = (error) => {
    setIsActionPending(false);
    setSwUpdateError(error.message);
  };

  const disableViewSwUpdateStatusButton = useCallback(() => {
    let disabled = false;
    const devices = (swUpdateAction?.devices && Object.values(swUpdateAction.devices)) || [];
    if (!swUpdateAction || devices.length === 0) return true;

    for (let device of devices) {
      const upgradeStatus = device?.upgrade_status?.status;
      if (!upgradeStatus || upgradeStatus === 'pending') {
        disabled = true;
        break;
      }
    }

    return disabled;
  }, [swUpdateAction]);

  const getMatchedStatusFromBulkSwUpdate = useCallback(() => {
    let matchedStatus = 'Pending';
    if (disableViewSwUpdateStatusButton()) return matchedStatus;

    const devices = (swUpdateAction?.devices && Object.values(swUpdateAction.devices)) || [];

    for (let device of devices) {
      if (device?.upgrade_status?.status === 'scheduled') {
        matchedStatus = device?.upgrade_status?.status;
      } else {
        matchedStatus = 'Updating';
        break;
      }
    }

    return capitalizeFirstLetter(matchedStatus);
  }, [swUpdateAction, disableViewSwUpdateStatusButton]);

  const areAllDevicesOnline = useCallback(() => {
    if (props.isGa2) return isDeviceOnline(props.device);
    // for echo overview page
    if (!isPivotHub) return isDeviceOnline(props.device);

    // for pivot overview page
    let allOnline = true;
    const members = (props.bundleMembers && Object.values(props.bundleMembers)) || [];
    if (members.length !== props.bundle?.members?.length) return false;

    for (let member of members) {
      if (!isDeviceOnline(member)) {
        allOnline = false;
        break;
      }
    }

    return allOnline;
  }, [props.bundleMembers, isPivotHub, props.device]);

  const disableSwUpdateButton = useCallback(
    () =>
      (isPivotHub && !props.bundle && !props.isGa2) ||
      isObjectEmpty(softwareVersions) ||
      !hasSwUpdatePermission ||
      !areAllDevicesOnline(),
    [props.bundle, softwareVersions, hasSwUpdatePermission, areAllDevicesOnline, isPivotHub]
  );

  const isSwUpdateDone = useCallback(() => {
    return TERMINAL_SW_UPDATE_STATUSES.includes(swUpdateAction?.runtime_status);
  }, [swUpdateAction]);

  return (
    <div
      id="BundleOverviewControllerHubDetailSwUpdateActionContainer"
      className="BundleOverviewControllerHubDetailSwUpdateActionContainer"
    >
      {swUpdateError && (
        <CommissionErrorBox title={'SW Update Error:'} message={swUpdateError} onClose={() => setSwUpdateError(null)} />
      )}
      {isActionDetailsModalOpen && swUpdateAction && (
        <ActionDetailsModal
          actionType={'BulkSoftwareUpdate'}
          action={swUpdateAction}
          actionTableType={'active'}
          setDetailsModalOpen={(isOpen) => setIsActionDetailsModalOpen(isOpen)}
          onClose={() => {
            isSwUpdateDone() && setSwUpdateAction(null);
            setIsActionDetailsModalOpen(false);
          }}
        />
      )}
      {isBulkSWUpdateModalOpen && (
        <BulkSoftwareDownloadModal
          key={0}
          cancelOnClick={handleSwUpdateModalOnClose} //this runs when the cancel button is clicked. Used to update store that modal has closed
          animateModalClose={false} //this determines whether closing modal performs animation
          selectedDevicesDictionary={
            !isPivotHub ? { [props.device?.id]: true } : { [props.bundle?.bundleId || props.device?.id]: true }
          }
          deviceIdList={!isPivotHub ? [props.device?.id] : [props.bundle?.bundleId || props.device?.id]}
          bundles={bundles}
          afterModalClose={handleSwUpdateModalOnClose}
          closeModal={false} //no external modal closing, this is only necessary if there is an animation that needs to happen before hiding the modal
          initiateBulkSwUpgrade={(sentSelectedDeviceIds, swId, transferDate, activationDate) =>
            handleInitiateBulkSwUpgrade(sentSelectedDeviceIds, swId, transferDate, activationDate)
          }
          softwareVersions={softwareVersions}
          actionPending={isActionPending}
          processError={(error) => handleSWDownloadError(error)}
          hideCheckbox={true}
        />
      )}
      {swUpdateAction == null ? (
        <div
          id="BundleOverviewControllerHubDetailSwUpdateButton"
          className="BundleOverviewControllerHubDetailSwUpdateButton"
        >
          <button
            disabled={disableSwUpdateButton()}
            className="Global_ThinBlueButton"
            id="Global_ThinBlueButton"
            onClick={() => {
              !disableSwUpdateButton() && setIsBulkSWUpdateModalOpen(true);
            }}
            data-tip={
              !hasSwUpdatePermission
                ? PERMISSION_ERROR_MESSAGE
                : !areAllDevicesOnline()
                ? 'All device(s) must be online to perform bulk software update action.'
                : 'SW Update'
            }
          >
            SW Update
          </button>
        </div>
      ) : getMatchedStatusFromBulkSwUpdate() === 'Scheduled' ? (
        <div
          id="BundleOverviewControllerHubDetailSwUpdateStatusBox"
          className="BundleOverviewControllerHubDetailSwUpdateStatusBox"
        >
          <IconAndText
            img={IconLoader.icon_schedule_bttn}
            hoverImg={IconLoader.icon_schedule_bttn}
            text={'Scheduled'}
            onClick={() => null}
          />
          <span
            id="BundleOverviewControllerHubDetailSwUpdateStatusBox_ViewLink"
            className={classnames({
              BundleOverviewControllerHubDetailSwUpdateStatusBox_ViewLink: !disableViewSwUpdateStatusButton(),
              BundleOverviewControllerHubDetailSwUpdateStatusBox_ViewLink_Disabled: disableViewSwUpdateStatusButton()
            })}
            data-tip="View details"
            onClick={() => !disableViewSwUpdateStatusButton() && setIsActionDetailsModalOpen(true)}
          >
            View details
          </span>
        </div>
      ) : (
        <div
          id="BundleOverviewControllerHubDetailSwUpdateStatusBox"
          className="BundleOverviewControllerHubDetailSwUpdateStatusBox"
        >
          {isSwUpdateDone() ? (
            <div
              id="BundleOverviewControllerHubDetailSwUpdateStatusBox_Status"
              className="BundleOverviewControllerHubDetailSwUpdateStatusBox_Status"
              data-tip={swUpdateAction?.runtime_status}
            >
              <span>{swUpdateAction?.runtime_status}</span>
            </div>
          ) : (
            <div
              id="BundleOverviewControllerHubDetailSwUpdateStatusBox_Status"
              className="BundleOverviewControllerHubDetailSwUpdateStatusBox_Status"
              data-tip={swUpdateAction?.runtime_status}
            >
              {BUTTON_LOADING_CIRCLE}
              <span>
                <LoadingIndicator text={getMatchedStatusFromBulkSwUpdate()} />
              </span>
            </div>
          )}
          <span
            id="BundleOverviewControllerHubDetailSwUpdateStatusBox_ViewLink"
            className={classnames({
              BundleOverviewControllerHubDetailSwUpdateStatusBox_ViewLink: !disableViewSwUpdateStatusButton(),
              BundleOverviewControllerHubDetailSwUpdateStatusBox_ViewLink_Disabled: disableViewSwUpdateStatusButton()
            })}
            data-tip={isSwUpdateDone() ? 'View details' : 'View progress'}
            onClick={() => {
              !disableViewSwUpdateStatusButton() && setIsActionDetailsModalOpen(true);
            }}
          >
            {isSwUpdateDone() ? 'View details' : 'View progress'}
          </span>
        </div>
      )}
    </div>
  );
};

export default memo(BundleOverviewControllerSwUpdateActionContainer);
